// SPDX-License-Identifier: BUSL-1.1
/**
 * Organizations and membership schema — Multi-tenancy support (CEO-V3).
 *
 */
import { pgTable, text, timestamp, uniqueIndex, uuid, check, index, integer, primaryKey } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
// ---------------------------------------------------------------------------
// ops_organizations
// ---------------------------------------------------------------------------
export const opsOrganizations = pgTable("ops_organizations", {
    id: uuid("id").notNull().defaultRandom(),
    name: text("name").notNull(),
    slug: text("slug").notNull().unique("ops_organizations_slug_key"),
    status: text("status").notNull().default("active"),
    /**
     * Per-org TTL override (days) for `ops_agent_activity` rows. NULL =
     * fall back to the global default (env `RETENTION_DAYS_ACTIVITY_DEFAULT`,
     * 90 days). Operational telemetry retention — shortenable by super_admin
     * only (the operator decides their own audit trail length, but cannot
     * shorten the platform-wide compliance baseline). CEO-V4 P4.
     */
    retentionDaysActivity: integer("retention_days_activity"),
    /**
     * Per-org TTL override (days) for `ops_audit_logs` rows. NULL = fall
     * back to the global default (env `RETENTION_DAYS_AUDIT_DEFAULT`,
     * 2555 days = 7y). Compliance baseline — shortenable by super_admin
     * only. CEO-V4 P4.
     */
    retentionDaysAudit: integer("retention_days_audit"),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_organizations_pkey" }),
    check("ops_organizations_status_check", sql `${t.status} IN ('active', 'paused', 'deleted')`),
    index("ops_organizations_slug_idx").on(t.slug),
    index("ops_organizations_status_idx").on(t.status),
]);
// ---------------------------------------------------------------------------
// ops_org_memberships — Polymorphic membership for humans and agents (CEO-V3)
// ---------------------------------------------------------------------------
export const opsOrgMemberships = pgTable("ops_org_memberships", {
    id: uuid("id").notNull().defaultRandom(),
    organizationId: uuid("organization_id")
        .notNull()
        .references(() => opsOrganizations.id, { onDelete: "cascade" }),
    principalId: text("principal_id").notNull(),
    principalType: text("principal_type").notNull(),
    /**
     * Better Auth `user.id` FK for `principal_type='human'` memberships.
     * NULL for agent/system principals. Adds typed-FK integrity + a fast
     * index path for sessionAuth claim validation that pre-Wave-3b had to
     * use `principal_id` text comparison + `.limit(1)`.
     *
     * CEO-V4 P3a schema bridge (cli-ceo issue #235). Migration 0033 adds
     * the column; the backfill script (`scripts/backfill-ba-org-bridge.ts`)
     * populates it from existing `principal_id` for human rows; the BA
     * dual-write hooks (PR2) keep it in sync going forward.
     */
    authUserId: text("auth_user_id"),
    role: text("role").notNull().default("member"),
    status: text("status").notNull().default("active"),
    createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
}, (t) => [
    primaryKey({ columns: [t.id], name: "ops_org_memberships_pkey" }),
    uniqueIndex("ops_org_memberships_org_principal_unique_idx").on(t.organizationId, t.principalType, t.principalId),
    check("ops_org_memberships_principal_type_check", sql `${t.principalType} IN ('human', 'agent', 'system')`),
    check("ops_org_memberships_role_check", sql `${t.role} IN ('owner', 'admin', 'member', 'viewer')`),
    check("ops_org_memberships_status_check", sql `${t.status} IN ('active', 'invited', 'removed')`),
    // CEO-V4 P3a #235: only `principal_type='human'` memberships may set
    // `auth_user_id`. Agent/system principals have no Better Auth user
    // record; setting the FK would dangle.
    check("ops_org_memberships_auth_user_id_human_only_check", sql `${t.principalType} = 'human' OR ${t.authUserId} IS NULL`),
    index("ops_org_memberships_principal_idx").on(t.principalType, t.principalId),
    index("ops_org_memberships_organization_idx").on(t.organizationId),
    index("ops_org_memberships_auth_user_id_idx").on(t.authUserId),
]);
//# sourceMappingURL=organizations.js.map